import {
    SvgoClinicFeatureUzi,
    SvgoClinicFeatureEcho,
    SvgoClinicFeatureEcg,
    SvgoClinicFeatureRentgen,
    SvgoClinicFeatureDentalxray,
    SvgoClinicFeatureCt,
    SvgoClinicFeatureMri,
    SvgoClinicFeatureEndo,
    SvgoClinicFeatureExpresslab,
    SvgoClinicFeatureOphthalmo,
    SvgoClinicFeatureOr,
    SvgoClinicFeatureGas,
    SvgoClinicFeatureLaparo,
    SvgoClinicFeatureLaser,
    SvgoClinicFeatureDentunit,
    SvgoClinicFeatureOxygen,
    SvgoClinicFeatureVent,
    SvgoClinicFeatureTransfusion,
    SvgoClinicFeatureChemo,
    SvgoClinicFeatureDaystationary,
    SvgoClinicFeatureStationary24,
    SvgoClinicFeatureInfection,
    SvgoClinicFeatureRehab,
    SvgoClinicFeatureCatfriendly,
    SvgoClinicFeatureRodents,
    SvgoClinicFeatureReptiles,
    SvgoClinicFeatureBirds,
    SvgoClinicFeatureCard,
    SvgoClinicFeatureInstallment,
    SvgoClinicFeatureInsurance,
    SvgoClinicFeaturePharmacy,
    SvgoClinicFeatureGrooming,
    SvgoClinicFeatureBoarding,
    SvgoClinicFeaturePettaxi,
    SvgoClinicFeatureChip,
    SvgoClinicFeatureTraveldocs,
    SvgoClinicFeatureShelters,
} from '#components';
import type { Component } from 'vue';

export type ClinicFeature = {
    icon: Component;
    /** Название из ЛК клиники. */
    name: string;
    /** Короткая расшифровка для подсказки в карточке. */
    hint: string;
};

/**
 * Оснащение клиники: иконка и подпись на каждый код из `features` листинга.
 * Порядок показа задаёт бэкенд (`ClinicFeatureCatalog::LISTING_PRIORITY`),
 * карточка берёт первые `CLINIC_FEATURES_IN_CARD`.
 */
export const CLINIC_FEATURES: Record<string, ClinicFeature> = {
    'uzi': { icon: SvgoClinicFeatureUzi, name: 'УЗИ', hint: 'УЗИ внутренних органов' },
    'echo': { icon: SvgoClinicFeatureEcho, name: 'ЭхоКГ', hint: 'УЗИ сердца' },
    'ecg': { icon: SvgoClinicFeatureEcg, name: 'ЭКГ', hint: 'Снятие ЭКГ' },
    'rentgen': { icon: SvgoClinicFeatureRentgen, name: 'Рентген', hint: 'Рентген-снимки' },
    'dentalxray': { icon: SvgoClinicFeatureDentalxray, name: 'Стоматологический рентген', hint: 'Снимки зубов' },
    'ct': { icon: SvgoClinicFeatureCt, name: 'КТ', hint: 'Компьютерная томография' },
    'mri': { icon: SvgoClinicFeatureMri, name: 'МРТ', hint: 'МРТ мягких тканей' },
    'endo': { icon: SvgoClinicFeatureEndo, name: 'Эндоскопия', hint: 'Осмотр камерой без разреза' },
    'expresslab': { icon: SvgoClinicFeatureExpresslab, name: 'Экспресс-лаборатория', hint: 'Анализы на месте' },
    'ophthalmo': { icon: SvgoClinicFeatureOphthalmo, name: 'Офтальмологическое оборудование', hint: 'Осмотр и лечение глаз' },
    'or': { icon: SvgoClinicFeatureOr, name: 'Операционная', hint: 'Есть операционный зал' },
    'gas': { icon: SvgoClinicFeatureGas, name: 'Ингаляционный наркоз', hint: 'Газовый наркоз' },
    'laparo': { icon: SvgoClinicFeatureLaparo, name: 'Лапароскопия', hint: 'Операции через проколы' },
    'laser': { icon: SvgoClinicFeatureLaser, name: 'Лазерная хирургия', hint: 'Операции лазером' },
    'dentunit': { icon: SvgoClinicFeatureDentunit, name: 'Стоматологическая установка', hint: 'Лечение зубов' },
    'oxygen': { icon: SvgoClinicFeatureOxygen, name: 'Кислородная камера / бокс', hint: 'Бокс с кислородом' },
    'vent': { icon: SvgoClinicFeatureVent, name: 'Аппарат ИВЛ', hint: 'Искусственная вентиляция' },
    'transfusion': { icon: SvgoClinicFeatureTransfusion, name: 'Переливание крови', hint: 'Переливание крови' },
    'chemo': { icon: SvgoClinicFeatureChemo, name: 'Химиотерапия', hint: 'Лечение опухолей' },
    'daystationary': { icon: SvgoClinicFeatureDaystationary, name: 'Дневной стационар', hint: 'Стационар на день' },
    '24stationary': { icon: SvgoClinicFeatureStationary24, name: 'Круглосуточный стационар', hint: 'Стационар на ночь' },
    'infection': { icon: SvgoClinicFeatureInfection, name: 'Инфекционный бокс', hint: 'Бокс для инфекций' },
    'rehab': { icon: SvgoClinicFeatureRehab, name: 'Реабилитация и физиотерапия', hint: 'Восстановление после травм' },
    'catfriendly': { icon: SvgoClinicFeatureCatfriendly, name: 'Cat Friendly', hint: 'Приём кошек без стресса' },
    'rodents': { icon: SvgoClinicFeatureRodents, name: 'Грызуны и кролики', hint: 'Приём грызунов и кроликов' },
    'reptiles': { icon: SvgoClinicFeatureReptiles, name: 'Рептилии и амфибии', hint: 'Приём рептилий' },
    'birds': { icon: SvgoClinicFeatureBirds, name: 'Птицы', hint: 'Приём птиц' },
    'card': { icon: SvgoClinicFeatureCard, name: 'Оплата картой', hint: 'Оплата картой' },
    'installment': { icon: SvgoClinicFeatureInstallment, name: 'Рассрочка', hint: 'Оплата частями' },
    'insurance': { icon: SvgoClinicFeatureInsurance, name: 'ДМС / страховка', hint: 'Принимают страховку' },
    'pharmacy': { icon: SvgoClinicFeaturePharmacy, name: 'Ветаптека', hint: 'Лекарства на месте' },
    'grooming': { icon: SvgoClinicFeatureGrooming, name: 'Груминг', hint: 'Стрижка и уход' },
    'boarding': { icon: SvgoClinicFeatureBoarding, name: 'Передержка', hint: 'Временное размещение' },
    'pettaxi': { icon: SvgoClinicFeaturePettaxi, name: 'Зоотакси', hint: 'Доставка в клинику' },
    'chip': { icon: SvgoClinicFeatureChip, name: 'Чипирование', hint: 'Электронная идентификация' },
    'traveldocs': { icon: SvgoClinicFeatureTraveldocs, name: 'Документы на вывоз', hint: 'Справки для поездок' },
    'shelters': { icon: SvgoClinicFeatureShelters, name: 'Льготные программы', hint: 'Приюты и льготы' },
};

export const CLINIC_FEATURES_IN_CARD = 5;

export function clinicFeature(code: string): ClinicFeature | null {
    return CLINIC_FEATURES[code] ?? null;
}
